import React from "react"
import * as b64 from "js-base64"
import * as layouts from "layouts"
import * as inputs from "inputs"
import * as icons from "icons"
import * as md5x from "md5x"
import * as api from "brandguard/api"
import * as training from "brandguard/display/training"

interface props extends layouts.containers.FlexProps {
	settings: api.ImageKernelFontEnabled
	onChange(upd: api.ImageKernelFontEnabled): void
}

const acceptedFonts = {
	"application/x-font-ttf": [".ttf"],
	"application/x-font-opentype": [".otf"],
	"application/font-woff": [".woff"],
	"application/font-woff2": [".woff2"],
	"application/vnd.ms-fontobject": [".eot"],
}

export default function FontDetection(props: React.PropsWithChildren<props>): JSX.Element {
	const { settings, onChange, ...rest } = props
	const updateFont = (md5: string, patch: Partial<api.ImageKernelFont>): void => {
		onChange({
			...settings,
			fonts: settings.fonts.map((f) => (f.md5 === md5 ? { ...f, ...patch } : f)),
		})
	}
	const fonts = (settings.fonts || []).map((l) => {
		const styleCount = [l.style_regular, l.style_bold, l.style_italic, l.style_light].filter(Boolean).length
		return (
			<layouts.Flex key={l.md5} flexDirection="column" py="5px">
				<layouts.Flex>
					<layouts.Span>{l.description}</layouts.Span>
					<layouts.Span
						ml="auto"
						onClick={(evt) => {
							evt.stopPropagation()
							onChange({ ...settings, fonts: settings.fonts.filter((o) => o.md5 !== l.md5) })
						}}
					>
						<icons.Trash width="14px" height="14px" />
					</layouts.Span>
				</layouts.Flex>
				<layouts.Flex pl="20px" pt="5px" gridGap="20px">
					<inputs.Checkbox
						checked={l.style_regular}
						onChange={() => updateFont(l.md5, { style_regular: !l.style_regular })}
						disabled={styleCount === 1 && l.style_regular}
					>
						<layouts.forms.Label my="unset" ml="5px">Regular</layouts.forms.Label>
					</inputs.Checkbox>
					<inputs.Checkbox
						checked={l.style_bold}
						onChange={() => updateFont(l.md5, { style_bold: !l.style_bold })}
						disabled={styleCount === 1 && l.style_bold}
					>
						<layouts.forms.Label my="unset" ml="5px">Bold</layouts.forms.Label>
					</inputs.Checkbox>
					<inputs.Checkbox
						checked={l.style_italic}
						onChange={() => updateFont(l.md5, { style_italic: !l.style_italic })}
						disabled={styleCount === 1 && l.style_italic}
					>
						<layouts.forms.Label my="unset" ml="5px">Italic</layouts.forms.Label>
					</inputs.Checkbox>
					<inputs.Checkbox
						checked={l.style_light}
						onChange={() => updateFont(l.md5, { style_light: !l.style_light })}
						disabled={styleCount === 1 && l.style_light}
					>
						<layouts.forms.Label my="unset" ml="5px">Light</layouts.forms.Label>
					</inputs.Checkbox>
				</layouts.Flex>
				<layouts.Flex pl="20px" pt="5px">
					<inputs.Text
						placeholder="usage rules (optional), e.g. only for Cow copy"
						defaultValue={l.usage}
						onChange={(evt) => updateFont(l.md5, { usage: evt.target.value })}
						className={training.layouts.styledTextInput}
					/>
				</layouts.Flex>
			</layouts.Flex>
		)
	})

	return (
		<layouts.Flex flexDirection="column" flex="1" {...rest}>
			<inputs.Dropwell
				multiple
				accept={acceptedFonts}
				onDrop={(accepted, rejected) => {
					if (accepted.length === 0) return
					const pending = Array.from(accepted).map((f) => {
						return f.arrayBuffer().then((b) => {
							const barray = new Uint8Array(b)
							return {
								mimetype: f.type,
								description: f.name,
								md5: md5x.string(barray),
								font: b64.fromUint8Array(barray),
								style_regular: true,
								style_bold: true,
								style_italic: true,
								style_light: true,
								usage: "",
							}
						})
					})

					Promise.all(pending).then((upd) => {
						const union = [
							...upd.filter((l) => (settings.fonts || []).find((o) => o.md5 === l.md5) === undefined),
							...(settings.fonts || []),
						]
						onChange({ ...settings, fonts: union })
					})
				}}
			>
				<layouts.dnd.Container>
					<icons.FileUpload />
					<layouts.dnd.Textual>Drag & drop your fonts here or click to browse files</layouts.dnd.Textual>
				</layouts.dnd.Container>
			</inputs.Dropwell>
			<layouts.Flex flexDirection="column" py="15px">
				{fonts}
			</layouts.Flex>
		</layouts.Flex>
	)
}
